import { useCallback, useMemo } from "react";
import { Button, Space, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { COLORS } from "../../../consts/index.js";
import { siteDayjs } from "../broccoliHarvestOptimiserDashboard/siteTimezone.js";
import { STATUS, STATUS_LABEL } from "./harvestEntryConstants.js";
import { convertKgToPound } from "../../../utils/poundConversions.js";
import {
  StatusEditCell,
  HarvestDateEditCell,
  YieldEditCell,
} from "./HarvestEntryEditors.jsx";

const solidTagStyle = (color) => ({
  backgroundColor: color,
  borderColor: color,
  color: "#ffffff",
  fontWeight: 700,
});

export function useBroccoliHarvestEntryColumns({
  ensurePendingDate,
  setDefaultHarvestDateISO,
  onAddPass,
  isLastPassOfBlock,
  siteTimezone,
  displayUnits,
}) {
  const getEffectiveStatus = useCallback(
    (row) => row.pendingStatus ?? row.status ?? STATUS.PLANTED,
    [],
  );

  const getEffectiveDate = useCallback(
    (row) => row.pendingHarvestDate ?? row.harvest_date ?? null,
    [],
  );

  const getEffectiveYield = useCallback(
    (row) =>
      row.pendingYield !== null && row.pendingYield !== undefined
        ? row.pendingYield
        : (row.yield_kg ?? null),
    [],
  );

  const rowHasPendingChanges = useCallback(
    (row) =>
      row.pendingStatus != null ||
      row.pendingHarvestDate != null ||
      row.pendingYield != null,
    [],
  );

  const columns = useMemo(
    () => [
      {
        key: "paddock_block",
        name: "Paddock / Block",
        frozen: true,
        width: "calc((100% - 88px) * 0.34)",
        minWidth: 200,
        renderHeaderCell: () => (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", alignItems: "center", height: "100%", fontWeight: 700, fontSize: 13, textAlign: "center", padding: "0 6px" }}>
            <div style={{ borderRight: "1px solid rgba(0,0,0,0.12)", paddingRight: 4 }}>Paddock</div>
            <div>Block</div>
          </div>
        ),
        renderCell: ({ row }) => (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", alignItems: "center", height: "100%", width: "100%", textAlign: "center" }}>
            <div style={{ fontWeight: 700, fontSize: 15, borderRight: "1px solid rgba(0,0,0,0.06)", paddingRight: 4 }}>{row.paddock}</div>
            <div>{row.block}</div>
          </div>
        ),
      },
      {
        key: "pass",
        name: "Pass",
        width: "calc((100% - 88px) * 0.09)",
        minWidth: 65,
        renderHeaderCell: () => (
          <div className="harvest-rdg-header-cell--center flex flex-col justify-center h-full">
            <span style={{ fontWeight: 700, fontSize: 13 }}>Pass</span>
          </div>
        ),
        renderCell: ({ row }) => (
          <div className="harvest-rdg-cell--center">
            {`Pass ${row.pass}`}
          </div>
        ),
      },
      {
        key: "pendingYield",
        name: displayUnits === "imperial" ? "Yield (lb)" : "Yield (ton)",
        width: "calc((100% - 88px) * 0.17)",
        minWidth: 90,
        editable: true,
        renderHeaderCell: () => (
          <div className="harvest-rdg-header-cell--center flex flex-col justify-center h-full">
            <span style={{ fontWeight: 700, fontSize: 13 }}>{displayUnits === "imperial" ? "Yield (lb)" : "Yield (ton)"}</span>
          </div>
        ),
        renderCell: ({ row }) => {
          const isPending = row.pendingYield != null;
          const effectiveYield = getEffectiveYield(row);
          // stored in tons (canonical unit); convert to display units for display
          const displayVal = effectiveYield !== null
            ? (displayUnits === "imperial" ? convertKgToPound(effectiveYield * 1000) : effectiveYield)
            : null;
          return (
            <div
              className="harvest-rdg-cell--center"
              style={{ fontStyle: isPending ? "italic" : "normal", fontWeight: 600 }}
            >
              {displayVal != null ? displayVal.toLocaleString() : <>&nbsp;</>}
            </div>
          );
        },
        renderEditCell: ({ row, onRowChange, onClose }) => (
          <YieldEditCell
            row={row}
            onRowChange={onRowChange}
            onClose={onClose}
            ensurePendingDate={ensurePendingDate}
            displayUnits={displayUnits}
          />
        ),
      },
      {
        key: "pendingHarvestDate",
        name: "Harvest Date",
        width: "calc((100% - 88px) * 0.20)",
        minWidth: 120,
        editable: true,
        editorOptions: { commitOnOutsideClick: false },
        renderHeaderCell: () => (
          <div className="harvest-rdg-header-cell--center flex flex-col justify-center h-full">
            <span style={{ fontWeight: 700, fontSize: 13 }}>Harvest Date</span>
          </div>
        ),
        renderCell: ({ row }) => {
          const date = getEffectiveDate(row);
          const isPending = row.pendingHarvestDate != null;
          return (
            <div
              className="harvest-rdg-cell--center"
              style={{ fontStyle: isPending ? "italic" : "normal", fontWeight: 600 }}
            >
              {date ? siteDayjs(date, siteTimezone).format("DD MMM, YYYY") : <>&nbsp;</>}
            </div>
          );
        },
        renderEditCell: ({ row, onRowChange, onClose }) => (
          <HarvestDateEditCell
            row={row}
            onRowChange={onRowChange}
            onClose={onClose}
            setDefaultHarvestDateISO={setDefaultHarvestDateISO}
            siteTimezone={siteTimezone}
          />
        ),
      },
      {
        key: "pendingStatus",
        name: "Status",
        width: "calc((100% - 88px) * 0.20)",
        minWidth: 120,
        editable: true,
        editorOptions: { commitOnOutsideClick: false },
        renderHeaderCell: () => (
          <div className="harvest-rdg-header-cell--center flex flex-col justify-center h-full">
            <span style={{ fontWeight: 700, fontSize: 13 }}>Status</span>
          </div>
        ),
        renderCell: ({ row }) => {
          const status = getEffectiveStatus(row);
          const isPending = rowHasPendingChanges(row);
          let tag;
          if (status === STATUS.HARVESTED) {
            tag = <Tag style={solidTagStyle(COLORS?.successGreen ?? "#32CD32")} className="m-0">{STATUS_LABEL[STATUS.HARVESTED]}</Tag>;
          } else if (status === STATUS.DISCARDED) {
            tag = <Tag style={solidTagStyle(COLORS?.dangerRed ?? "#fa634f")} className="m-0">{STATUS_LABEL[STATUS.DISCARDED]}</Tag>;
          } else {
            tag = <Tag color="default" className="m-0">{STATUS_LABEL[STATUS.PLANTED]}</Tag>;
          }
          return (
            <div className="harvest-rdg-cell--center">
              <Space size={6} align="center" style={{ justifyContent: "center" }}>
                {tag}
                {isPending && (
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: COLORS?.primaryBlue ?? "#1677ff" }} />
                )}
              </Space>
            </div>
          );
        },
        renderEditCell: ({ row, onRowChange, onClose }) => (
          <StatusEditCell
            row={row}
            onRowChange={onRowChange}
            onClose={onClose}
            getEffectiveStatus={getEffectiveStatus}
            ensurePendingDate={ensurePendingDate}
          />
        ),
      },
      {
        key: "actions",
        name: "",
        width: 44,
        minWidth: 44,
        resizable: false,
        renderHeaderCell: () => null,
        renderCell: ({ row }) => {
          if (!isLastPassOfBlock(row)) return null;
          const atMax = row.pass >= 10;
          return (
            <div className="flex items-center justify-center h-full">
              <Button
                size="small"
                icon={<PlusOutlined />}
                disabled={atMax}
                title={atMax ? "Maximum 10 passes reached" : "Add next pass"}
                onClick={(e) => {
                  e.stopPropagation();
                  onAddPass(row);
                }}
              />
            </div>
          );
        },
      },
    ],
    [
      ensurePendingDate,
      setDefaultHarvestDateISO,
      getEffectiveStatus,
      getEffectiveDate,
      getEffectiveYield,
      rowHasPendingChanges,
      onAddPass,
      isLastPassOfBlock,
      siteTimezone,
      displayUnits,
    ],
  );

  return { columns, rowHasPendingChanges, getEffectiveStatus };
}
